import React, { useCallback, useMemo } from 'react';
import { useRef } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { ReportsContext } from 'apps/sigma2-ui/src/reports/hooks/ReportsContext';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { GenerateReportControllerResult } from 'libs/end-to-end/src/generate-report';
import { QueryReportControllerResult } from 'libs/end-to-end/src/generate-report';
import { GenerateReportRequest } from 'libs/end-to-end/src/generate-report';
import { useSessionStorage } from 'apps/sigma2-ui/src/lib/hooks/useSessionStorage';
import { z } from 'zod';
import { PendingReportInfo } from 'apps/sigma2-ui/src/reports/hooks/types';
import { waitForMs } from 'libs/end-to-end/src/generic';
import { usePolling } from 'apps/sigma2-ui/src/lib/hooks/usePolling';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';

interface ReportsFinishedCallback {
  reportId: string;
  fn: (
    reportId: string,
    status: 'success' | 'failure',
    presignedUrl: string | null,
  ) => void;
}
export interface ReportsFinishedCallbacks {
  callbacks: ReportsFinishedCallback[];
}

export const ReportsRenderer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { userAuthenticatedApiCall, isAuthenticated } = useUser();

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const finishedCallbacks = useRef<ReportsFinishedCallbacks>({
    callbacks: [],
  });

  const [pendingReports, setPendingReports] = useSessionStorage({
    key: 'PendingReportsList',
    schema: z.object({ list: z.array(PendingReportInfo) }),
    defaultValue: { list: [] },
  });

  const exportReport = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      request: GenerateReportRequest,
      onStarted?: (reportId: string) => void,
      onFinished?: (
        reportId: string,
        status: 'success' | 'failure',
        presignedUrl: string | null,
      ) => void,
    ) => {
      if (
        !isAlive() ||
        !isAuthenticated ||
        !selectedOrganizationId ||
        !selectedDatabaseId ||
        !selectedDatabaseRevisionId ||
        request.charts.length === 0
      ) {
        return;
      }

      const result = await userAuthenticatedApiCall({
        responseSchema: GenerateReportControllerResult,
        url: `${getApiBaseUrl()}/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}/reports`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(request),
        },
      });

      if (!isAlive() || !result || result.resultType === 'err') {
        return;
      }

      const reportId = result.value.reportId;

      if (onStarted) {
        onStarted(reportId);
      }

      // Add the report to the pending reports list.
      setPendingReports((prev) => {
        return {
          list: [
            ...prev.list,
            {
              reportId,
              organizationId: selectedOrganizationId,
              databaseId: selectedDatabaseId,
              databaseRevisionId: selectedDatabaseRevisionId,
              status: 'pending',
              presignedUrl: null,
            },
          ],
        };
      });

      if (onFinished) {
        finishedCallbacks.current.callbacks.push({
          reportId,
          fn: onFinished,
        });
      }
    },
    [
      isAuthenticated,
      selectedDatabaseId,
      selectedDatabaseRevisionId,
      selectedOrganizationId,
      setPendingReports,
      userAuthenticatedApiCall,
    ],
  );

  const removePendingReport = useCallback(
    (reportId: string) => {
      setPendingReports((prev) => ({
        list: prev.list.filter((report) => report.reportId !== reportId),
      }));
    },
    [setPendingReports],
  );

  const isAlive = useAlive();

  const updatePendingReportsFn = useMemo(() => {
    return async () => {
      if (!isAlive()) {
        return;
      }

      const reportIds = pendingReports.list
        .filter(
          (report) =>
            report.organizationId === selectedOrganizationId &&
            report.databaseId === selectedDatabaseId &&
            report.databaseRevisionId === selectedDatabaseRevisionId,
        )
        .map((report) => report.reportId);

      if (reportIds.length === 0) {
        return;
      }

      const newReportsStatus: {
        list: PendingReportInfo[];
      } = { list: [...pendingReports.list] };

      // Loop through the reports and fetch status
      for (const reportId of reportIds) {
        const pollResult = await userAuthenticatedApiCall({
          responseSchema: QueryReportControllerResult,
          url: `${getApiBaseUrl()}/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}/reports/${reportId}`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (!isAlive() || !pollResult || pollResult.resultType === 'err') {
          return;
        }

        // Prevent too many requests
        await waitForMs(50);

        if (!isAlive()) {
          return;
        }

        const newStatus = pollResult.value.reportStatus;
        const newPresignedUrl = pollResult.value.presignedUrl;

        if (newStatus === 'success' || newStatus === 'failure') {
          // Call the finished callback if it has been registered (this is for the synchronous case)
          const callbackIndex = finishedCallbacks.current.callbacks.findIndex(
            (callback) => callback.reportId === reportId,
          );

          if (callbackIndex !== -1) {
            const callback = finishedCallbacks.current.callbacks[callbackIndex];
            callback.fn(reportId, newStatus, newPresignedUrl);
            finishedCallbacks.current.callbacks.splice(callbackIndex, 1);
          }
        }

        const reportIndex = newReportsStatus.list.findIndex(
          (report) => report.reportId === reportId,
        );

        if (reportIndex !== -1) {
          newReportsStatus.list[reportIndex] = {
            ...newReportsStatus.list[reportIndex],
            status: newStatus,
            presignedUrl: newPresignedUrl,
          };
        }
      }

      setPendingReports(() => newReportsStatus);
    };
  }, [
    isAlive,
    pendingReports.list,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
    setPendingReports,
    userAuthenticatedApiCall,
  ]);

  usePolling({
    action: updatePendingReportsFn,
    delay: 5000,
    // We do our state setting in the action itself.
    onReceivedResult: () => {},
  });

  return (
    <ReportsContext.Provider
      value={{
        exportReport,
        pendingReports: pendingReports.list,
        removePendingReport,
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};
